import React, { useState } from 'react'
import styled from 'styled-components'
import SessionDialog from '../../sessions/components/SessionDialog'

const Field = styled.div`
  margin-bottom: 12px;
`
const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  margin-bottom: 4px;
`
const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  font-family: inherit;
  font-size: 14px;
`
const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  font-family: inherit;
  font-size: 14px;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
`
const FileBtn = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  &:hover {
    background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  }
`
const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`

/**
 * Two-step upload UX (matches backend semantics — see B3 doc):
 *   1. User picks a file. The frontend uploads it via the existing media
 *      pipeline (placeholder here — uses object URL in mock mode) to obtain
 *      assetUrl + sizeBytes + mimeType.
 *   2. User adds metadata (category, description) and confirms.
 *
 * In mock mode, we synthesize an assetUrl from URL.createObjectURL so the
 * listing renders a real download link. Real backend uses signed-URL flow.
 */
export default function UploadDocumentDialog({ open, onClose, onSubmit }) {
  const [file, setFile] = useState(null)
  const [category, setCategory] = useState('LAB_REPORT')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)

  function handleFile(e) {
    const f = e.target.files?.[0]
    setFile(f || null)
  }

  async function handle() {
    if (!file) return
    setBusy(true)
    try {
      // In real mode, the frontend would PUT the file to a signed URL here and
      // use the returned canonical URL. For F6 mock we use objectURL.
      const assetUrl = URL.createObjectURL(file)
      await onSubmit({
        fileName: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        assetUrl,
        category,
        description: description.trim() || null,
      })
      setFile(null)
      setDescription('')
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <SessionDialog
      open={open}
      title="Upload a document"
      subtitle="Lab reports, imaging, referral letters — keep them all in one place."
      primaryLabel="Upload"
      primaryDisabled={!file}
      primaryLoading={busy}
      onPrimary={handle}
      onClose={onClose}
    >
      <Field>
        <Label>File</Label>
        <FileBtn>
          📎 {file ? file.name : 'Choose file'}
          <HiddenInput type="file" onChange={handleFile} />
        </FileBtn>
      </Field>
      <Field>
        <Label>Category</Label>
        <Select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="LAB_REPORT">Lab report</option>
          <option value="IMAGING">Imaging</option>
          <option value="REFERRAL">Referral</option>
          <option value="INSURANCE">Insurance</option>
          <option value="OTHER">Other</option>
        </Select>
      </Field>
      <Field>
        <Label>Description (optional)</Label>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. Pre-visit lipid panel"
        />
      </Field>
    </SessionDialog>
  )
}
